import * as React from 'react';
import {
  Switch,
  Text,
  Subtitle2,
  makeStyles,
  shorthands,
  tokens,
  mergeClasses,
} from '@fluentui/react-components';

export interface ToggleSettingsItem<TKey extends string = string> {
  key: TKey;
  label: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
}

export interface ToggleSettingsMaster {
  label: React.ReactNode;
  description?: React.ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

export interface ToggleSettingsListProps<TKey extends string = string> {
  items: ToggleSettingsItem<TKey>[];
  values: Record<TKey, boolean>;
  onChange: (key: TKey, checked: boolean) => void;
  master?: ToggleSettingsMaster;
  header?: React.ReactNode;
  headerIcon?: React.ReactNode;
  disabled?: boolean;
  footer?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
  headerIcon: {
    color: tokens.colorBrandForeground1,
    display: 'inline-flex',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.gap('16px'),
    ...shorthands.padding('12px', '0'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  childRow: {
    paddingLeft: '24px',
  },
  rowText: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('2px'),
  },
  description: {
    color: tokens.colorNeutralForeground3,
  },
  footer: {
    display: 'flex',
    ...shorthands.gap('8px'),
  },
});

function ToggleSettingsList<TKey extends string = string>({
  items,
  values,
  onChange,
  master,
  header,
  headerIcon,
  disabled,
  footer,
  className,
  style,
}: ToggleSettingsListProps<TKey>) {
  const styles = useStyles();
  const childrenDisabled = disabled || (master != null && !master.checked);

  return (
    <div className={className} style={style}>
      <div className={styles.root}>
        {header != null &&
          (typeof header === 'string' ? (
            <div className={styles.header}>
              {headerIcon && (
                <span className={styles.headerIcon}>{headerIcon}</span>
              )}
              <Subtitle2>{header}</Subtitle2>
            </div>
          ) : (
            header
          ))}

        <div className={styles.list}>
          {master != null && (
            <div className={styles.row}>
              <div className={styles.rowText}>
                <Text weight="semibold">{master.label}</Text>
                {master.description != null && (
                  <Text size={200} className={styles.description}>
                    {master.description}
                  </Text>
                )}
              </div>
              <Switch
                checked={master.checked}
                disabled={disabled || master.disabled}
                onChange={(_, data) => master.onChange(data.checked)}
              />
            </div>
          )}
          {items.map((item) => (
            <div
              key={item.key}
              className={mergeClasses(
                styles.row,
                master != null && styles.childRow
              )}
            >
              <div className={styles.rowText}>
                <Text>{item.label}</Text>
                {item.description != null && (
                  <Text size={200} className={styles.description}>
                    {item.description}
                  </Text>
                )}
              </div>
              <Switch
                checked={!!values[item.key]}
                disabled={childrenDisabled || item.disabled}
                onChange={(_, data) => onChange(item.key, data.checked)}
              />
            </div>
          ))}
        </div>

        {footer != null && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>
  );
}

export default ToggleSettingsList;
